import React, { useEffect, useState } from "react";
import { Col, Row, Form, Modal } from "react-bootstrap";
import PropTypes from 'prop-types';
import { Button, SvgIcon } from "@mui/material";
import { EmojiObjects as EmojiObjectsIcon } from '@mui/icons-material';
import { FormProvider, useForm } from 'react-hook-form';
import { number, object, string } from 'yup';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import Age from '../../../Helpers/Dates/Age';
import NeedsAnalysisFooter from "../../../Tools/NeedsAnalysis/NeedsAnalysisFooter";
import AnalysisData from "../../../Helpers/Constants/AnalysisData";
import { yupResolver } from "@hookform/resolvers/yup";
import { getSessionStorageItem, SESSION_STORAGE_ANALYSIS_KEY, SESSION_STORAGE_BASIC_INFO_KEY } from "../../../../sessionStorage";
import "./TermLengthCalc.scss";

export default function TermLengthAnalysis(props) {

  const basicInfo = getSessionStorageItem(SESSION_STORAGE_BASIC_INFO_KEY);
  const sessionStorageFormState = getSessionStorageItem(SESSION_STORAGE_ANALYSIS_KEY);

  const initialFormState = sessionStorageFormState || AnalysisData();

  const [formValues, setFormValues] = useState(initialFormState);
  const [suggestedTermLength, setSuggestedTermLength] = useState(null);
  const formMethods = useForm({
    resolver: yupResolver(TermLengthAnalysis.ValidationSchema(Age(basicInfo && basicInfo.birthdate))),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formValues
  });

  const { handleSubmit, trigger } = formMethods;

  const calculateTermLength = () => {
    let termLength = 20;
    const insuredAge = (props.fields && props.fields.insuredAge) || null;

    if (insuredAge) {
      termLength = (insuredAge >= 60) ? 10 : 20;
    }

    setSuggestedTermLength(termLength);
  };

  const applyTermLength = () => {
    if (suggestedTermLength === null || !props.onTermLengthCalculation instanceof Function) {
      return;
    }

    props.onTermLengthCalculation(suggestedTermLength);
  };

  useEffect(() => {
    calculateTermLength();
  }, [formValues]);

  const handleInputChange = (fieldName, value) => {
    const formStateToUpdate = { ...formValues };
    formStateToUpdate[fieldName] = value;
    setFormValues({ ...formStateToUpdate });
    trigger(fieldName);
    props.setAnalysisFormValues({ ...formStateToUpdate });
  };

  const handleChildrenNumberChange = (fieldName, value) => {
    const newChild = formValues.numberOfChildren === "" ? 0 : formValues.numberOfChildren;
    const formStateToUpdate = { ...formValues };

    if (newChild === 0 || newChild < value) {
      for (let i = newChild; i < value; i++) {
        formStateToUpdate.children.push({ childAge: '' });
      }
    } else {
      const len = newChild - 1;
      for (let i = len; i >= value; i--) {
        formStateToUpdate.children.splice(i, 1);
      }
    }

    formStateToUpdate[fieldName] = value;

    setFormValues({ ...formStateToUpdate });
    props.setAnalysisFormValues({ ...formStateToUpdate });
  };

  function handleClick() {
    applyTermLength();
    sessionStorage.setItem(SESSION_STORAGE_ANALYSIS_KEY, JSON.stringify({ ...formValues }));
    props.fields.setModalTermLengthCalculatorIsOpen(false);
  }

  return (
    <React.Fragment>
      <TermLengthAnalysis.ModalHeroImageHeader suggestedTermLength={suggestedTermLength} isPrequote={props.isPrequote}
        onButtonClick={() => handleClick()} />
      <FormProvider {...formMethods}>
        <Form className='modal-scroll' onSubmit={() => handleSubmit(handleClick())}>
          <Row className="terms-calculator justify-content-center">
            <Col lg={8} className="col-12 flex-grow-0">
              <div className="mb-0 mt-4">
                <Col lg={12}>
                  <StyledRadioInput
                    name="maritalStatus"
                    id="maritalStatus"
                    value={formValues.maritalStatus}
                    options={[{ value: "Married", label: "Married" }, { value: "Single", label: "Single" }]}
                    onChange={e => handleInputChange('maritalStatus', e)}
                    isWhiteBackground={true}
                    isBlankIcon={true}
                  />
                </Col>
              </div>
              <div className="mb-3">
                <Col lg={12}>
                  <SelectInput
                    name="numberOfChildren"
                    id="numberOfChildren"
                    value={formValues.numberOfChildren}
                    onChange={(e) => handleChildrenNumberChange('numberOfChildren', e.target.value)}
                    label="Number of Children"
                    selectOptions={[
                      { value: 0, label: "None" },
                      { value: 1, label: "1" },
                      { value: 2, label: "2" },
                      { value: 3, label: "3" },
                      { value: 4, label: "4" },
                      { value: 5, label: "5" },
                      { value: 6, label: "6" },
                      { value: 7, label: "7" },
                      { value: 8, label: "8" },
                    ]}
                  />
                </Col>
              </div>
              {formValues.numberOfChildren > 0 &&
                <div className="mb-3">
                  <Col lg={12}>
                    <TextInput
                      name="childAge"
                      id="childAge"
                      label="Age of Youngest Child"
                      value={formValues.childAge}
                      onChange={(e) => handleInputChange('childAge', e.target.value)}
                    />
                  </Col>
                </div>
              }
              <div className="mb-3">
                <Col lg={12}>
                  <StyledRadioInput
                    name="mortgage"
                    id="mortgage"
                    label='Do you have a mortgage?'
                    value={formValues.mortgage}
                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                    onChange={e => handleInputChange('mortgage', e)}
                    isWhiteBackground={true}
                    isBlankIcon={true}
                  />
                </Col>
              </div>
              {formValues.mortgage === "Yes" && (
                <React.Fragment>
                  <div className="mb-3">
                    <Col lg={12}>
                      <TextInput
                        name="mortgageTerm"
                        id="mortgageTerm"
                        label="Remaining Mortgage Term (Years)"
                        value={formValues.mortgageTerm}
                        onChange={(e) => handleInputChange('mortgageTerm', e.target.value)}
                      />
                    </Col>
                  </div>
                  <div className="tooltip-box mb-2 m-3">
                    <span className="spanTip"><SvgIcon style={{ fontSize: '1rem', marginTop: '-4px' }} color="primary"
                      component={EmojiObjectsIcon} />Insider Tip:</span> Most people
                    choose to protect the remaining balance and years remaining on their mortgage.
                  </div>
                </React.Fragment>
              )}
              <div className="mt-4 mb-3">
                <Col lg={12}>
                  <TextInput
                    name="longestDebt"
                    id="longestDebt"
                    label="Longest Remaining Debt (Years)"
                    value={formValues.longestDebt}
                    onChange={(e) => handleInputChange('longestDebt', e.target.value)}
                  />
                </Col>
              </div>
              <div className="justify-content-center mb-3">
                <Col lg={12}>
                  <Button type="button" color="primary" variant="contained" onClick={() => handleClick()} fullWidth>
                    UPDATE QUOTE
                  </Button>
                </Col>
              </div>
            </Col>
          </Row>
          < NeedsAnalysisFooter isCustomTerm={props.isCustomTerm} />
        </Form>
      </FormProvider>
    </React.Fragment>
  );
}

TermLengthAnalysis.ModalHeroImageHeader = function (props) {
  return (
    <Modal.Header closeButton>
      <div className="modalImageHeader">
        <div className="termCalculatorHeroImage" />
        <div className="headerModalDiv">
          <p id="coverageNeed">My Insurance Term</p>
          <p id="coverageNeed"><b>{props.suggestedTermLength ? props.suggestedTermLength : '/'} years of coverage</b>
          </p>
          <Button type="button"
            variant="outlined"
            onClick={() => props.onButtonClick()}
            className="btn-wide-sm button-white">UPDATE QUOTE</Button>
        </div>
      </div>
    </Modal.Header>
  );
};

TermLengthAnalysis.propTypes = {
  fields: PropTypes.object,
  isPrequote: PropTypes.bool,
  onTermLengthCalculation: PropTypes.func,
  isCustomTerm: PropTypes.bool,
};

TermLengthAnalysis.ModalHeroImageHeader.propTypes = {
  onButtonClick: PropTypes.func,
  suggestedTermLength: PropTypes.number,
};

TermLengthAnalysis.ValidationSchema = (currentAge) => {
  return object().shape({
    maritalStatus: string().notRequired(),
    numberOfChildren: string().notRequired(),
    childAge: string().when('numberOfChildren', {
      is: (numberOfChildren) => parseInt(numberOfChildren) > 0,
      then: (schema) => schema.required("Child Age is required").max(2, 'Invalid Age.').test({
        message: 'Invalid Age.',
        test: (value) => value >= 0
      }).test({
        message: `Child's age cannot be greater than parent's age.`,
        test: (value) => isNaN(currentAge) ? true : currentAge > value
      }),
      otherwise: (schema) => schema.notRequired()
    }),
    mortgage: string().notRequired(),
    mortgageTerm: number().transform((v) => (v === '' || v === null || v === undefined) ? 0 : v)
      .when('mortgage', {
        is: (mortgage) => typeof mortgage === "string" && mortgage.toLowerCase() === 'yes',
        then: (schema) =>
          schema.required('Enter remaining mortgage term in years')
            .typeError('Enter remaining mortgage term in number of years')
            .min(1, 'Mortgage term must be greater than one year')
            .max(40, 'Mortgage term must not be greater than 40 years'),
        otherwise: (schema) => schema.notRequired(),
      }),
    longestDebt: number().transform((v) => (v === '' || v === null || v === undefined) ? 0 : v)
      .typeError('Enter remaining debt in number of years')
      .min(1, 'Longest remaining debt must be greater than one year')
      .max(100, 'Longest remaining debt must not be greater than 100 years')
  });
};
